import React, { useState, useEffect } from 'react';

const PaymentSection = () => {
    const [reservations, setReservations] = useState([]);

    // Load reservations from localStorage on component mount
    useEffect(() => {
        const savedReservations = localStorage.getItem('trip-reservations');
        if (savedReservations) {
            try {
                const parsedReservations = JSON.parse(savedReservations);
                setReservations(parsedReservations);
            } catch (error) {
                console.error('Error loading saved reservations:', error);
            }
        }
    }, []);

    // Save reservations to localStorage whenever reservations changes
    useEffect(() => {
        if (reservations.length > 0) {
            localStorage.setItem('trip-reservations', JSON.stringify(reservations));
        }
    }, [reservations]);

    // Listen for payment success messages from iframe
    useEffect(() => {
        const handleMessage = (event) => {
            // Check if message is from payment iframe
            if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
                const newReservation = {
                    id: generateReservationId(),
                    date: new Date().toLocaleDateString(),
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    timestamp: new Date().toISOString()
                };
                setReservations(prev => [newReservation, ...prev]);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const generateReservationId = () => {
        return 'RES-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5).toUpperCase();
    };

    return (
        <>
            {/* Separator line */}
            <div className="separator-container my-8">
                <div className="flex items-center">
                    <div className="flex-grow h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
                    <div className="px-4 text-sm text-gray-500 font-medium bg-gray-100 rounded-full"></div>
                    <div className="flex-grow h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent"></div>
                </div>
            </div>

            <div className="payment-section-wrapper mb-8 flex justify-center" id="payment-section" data-test-id="payment-section">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow-lg p-6 w-full max-w-2xl border-l-4 border-purple-500">
                    <h3 className="text-2xl font-bold mb-4 text-center text-purple-800">Next Trip Reservation</h3>
                    <p className="text-gray-700 text-center mb-6 text-sm font-medium">
                        Book and pay for your next adventure with secure payment processing
                    </p>

                    {/* Payment iframe */}
                    <div className="payment-iframe-container bg-white rounded-lg p-4 shadow-inner border border-purple-100">
                        <iframe
                            src="/payment-iframe.html"
                            className="w-full border-2 border-purple-200 rounded-lg payment-iframe"
                            id="reservation-payment-iframe"
                            data-test-id="reservation-payment-iframe"
                            title="Next Trip Reservation Payment"
                            style={{ height: '400px' }}
                        ></iframe>
                    </div>
                </div>
            </div>

            {/* Reservations Table */}
            {reservations.length > 0 && (
                <div className="reservations-table-section bg-white rounded-lg shadow-lg p-6 mb-8 border-l-4 border-green-500" id="reservations-table-section" data-test-id="reservations-table-section">
                    <h3 className="text-2xl font-bold mb-6 text-center text-gray-800">Recent Reservations</h3>
                    <div className="table-wrapper bg-white rounded-lg shadow">
                        <table className="w-full table" id="reservations-table" data-test-id="reservations-table">
                            <thead className="bg-green-50 table-header">
                                <tr className="header-row">
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" id="header-reservation-id" data-test-id="header-reservation-id">
                                        Reservation ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" id="header-reservation-date" data-test-id="header-reservation-date">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" id="header-reservation-time" data-test-id="header-reservation-time">
                                        Time
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" id="header-reservation-status" data-test-id="header-reservation-status">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200 table-body">
                                {reservations.map((reservation, index) => (
                                    <tr key={reservation.id} className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`} id={`reservation-row-${reservation.id}`} data-test-id={`reservation-row-${index}`}>
                                        <td className="px-6 py-4 whitespace-nowrap cell cell-reservation-id" data-test-id={`cell-reservation-id-${index}`}>
                                            <span className="font-medium text-green-700">{reservation.id}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap cell cell-reservation-date" data-test-id={`cell-reservation-date-${index}`}>
                                            <span className="text-gray-900">{reservation.date}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap cell cell-reservation-time" data-test-id={`cell-reservation-time-${index}`}>
                                            <span className="text-gray-900">{reservation.time}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap cell cell-reservation-status" data-test-id={`cell-reservation-status-${index}`}>
                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-medium">
                                                Confirmed
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};

export default PaymentSection;